







































import { Vue, Prop, Component } from 'vue-property-decorator';
import ThemedButton from '@lib-shared/components/ThemedButton.vue';
import { ERecipientStatus } from '@lib-shared/recipients/types/enums/recipient-status';
import { ERecipientRole } from '@lib-shared/recipients/types/enums/recipient-role';
import { EDocumentStatus } from '@signing-page/documents/types/enums/document-status';
import { IRecipient } from '@signing-page/documents/types/recipient';
import { ITheme } from '@signing-page/entity/types/theme';
import { IDocument } from '@signing-page/documents/types/document';
import { SigningHelper } from '@lib-shared/helpers/signing.helper';
import { translate } from '@lib-shared/helpers/translation.helper';
import ScrollDownToSignButton from '@signing-page/signing/components/ScrollDownToSignButton.vue';

@Component({
  components: {
    ThemedButton,
    ScrollDownToSignButton,
  },
  data: () => ({
    EDocumentStatus,
    ERecipientStatus,
    ERecipientRole,
  }),
})
export default class SignActions extends Vue {
  @Prop() document: IDocument;
  @Prop() recipient: IRecipient;
  @Prop() requiredIds: string[];
  @Prop() nextRequiredId: string;
  @Prop() focusedRequiredId: string;
  @Prop() theme: ITheme;
  @Prop() isSigningEnabled: boolean;
  @Prop() startEid: boolean;
  @Prop() disableSignButton: boolean;

  get recipientIsSigner(): boolean {
    return this.recipient.role === ERecipientRole.Signer;
  }

  get showRequiredSteps(): boolean {
    return !this.hasRecipientSigned && this.hasRequiredSteps;
  }

  get hasRecipientSigned(): boolean {
    return SigningHelper.hasRecipientSigned(this.recipient.status);
  }

  get hasRequiredSteps(): boolean {
    return this.requiredIds.length > 0 && !!this.nextRequiredId;
  }

  get requiredStepsButtonText(): string {
    return this.focusedRequiredId ? 'next' : 'show-required';
  }

  get requiredStepsMessage(): string {
    return this.requiredIds.length === 1 ? 'you-have-a-required-step' : 'you-have-required-steps';
  }

  get showSignButton(): boolean {
    return this.recipient.status === ERecipientStatus.Reviewed && this.isSigningEnabled;
  }

  get signButtonText(): string {
    if (this.recipientIsSigner) {
      return this.theme?.signButtonText || 'sign-document';
    }
    return 'approve-document';
  }

  get actionMessage(): string {
    return this.recipientIsSigner ? 'scroll-down-to-sign' : 'scroll-down-to-approve';
  }

  get showActionMessage(): boolean {
    if (this.recipient.status === ERecipientStatus.Reviewed || this.hasRecipientSigned) {
      return false;
    }
    return this.isSigningEnabled;
  }

  get message(): string {
    if (this.recipient.role === ERecipientRole.CC) {
      return '';
    }
    if (this.document.status === EDocumentStatus.Signed) {
      return 'document-is-already-signed';
    }
    if (this.recipient.status === ERecipientStatus.Rejected) {
      return 'document-has-been-rejected';
    }
    if (this.recipientIsSigner && this.hasRecipientSigned) {
      return 'has-already-signed-document';
    }
    if (this.recipient.role === ERecipientRole.Approver && this.hasRecipientSigned) {
      return 'has-already-approved';
    }
    if (!this.isSigningEnabled) {
      return 'waiting-for-other-signers';
    }
    return '';
  }

  get tooltip() {
    if (!this.disableSignButton) {
      return null;
    }
    return {
      content: translate('uploading-attachments-signing-disabled').toString(),
      offset: 12,
      trigger: 'hover',
      placement: 'top',
      delay: { show: 300, hide: 100 },
    };
  }

  scrollToBottom() {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  }

  showRequiredStep(nextRequiredId: string) {
    if (!nextRequiredId) {
      return;
    }

    const nextElement: HTMLElement = document.getElementById(this.nextRequiredId);
    if (!nextElement) {
      return;
    }
    nextElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.$emit('updateFocusedRequiredId', this.nextRequiredId);
  }

  sign() {
    this.recipientIsSigner ? this.$emit('sign') : this.$emit('approve');
  }

  mounted() {
    if (this.startEid) {
      this.$emit('sign');
    }
  }
}
